import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getSaDashboardMetrics } from "../helpers/super_admin_ops_helper";
import { openTwoColumnSubmenu } from "./twoColumnPanel";

type SaMenuBadges = {
  sends_awaiting_sa_resume: number;
  sends_canary_paused: number;
  circuits_open: number;
  tenants_at_risk: number;
  tenants_at_daily_cap: number;
  host_stale: boolean;
  host_warn: boolean;
  dns_glue_ok: boolean;
  mp_webhook_secret_alert: boolean;
};

/**
 * Menu Super Admin — Dashboard, Ops, Reputação, Tenants, Comercial (planos+cupons), Modelos (DOC 09 § 6.2).
 * Labels = chaves i18n; TwoColumnLayout aplica t().
 * Badges: poll metrics a cada 60s (mesmo ritmo do dashboard em freeze) — LAC-092/097.
 */
const SuperAdminNavdata = () => {
  const history = useNavigate();
  const [isDashboard, setIsDashboard] = useState(false);
  const [isOps, setIsOps] = useState(false);
  const [isReputation, setIsReputation] = useState(false);
  const [isTenants, setIsTenants] = useState(false);
  const [isPlans, setIsPlans] = useState(false);
  const [isPlatformTemplates, setIsPlatformTemplates] = useState(false);
  const [iscurrentState, setIscurrentState] = useState("Dashboard");
  const [badges, setBadges] = useState<SaMenuBadges | null>(null);

  function updateIconSidebar(e: any) {
    if (e?.target?.getAttribute("sub-items")) {
      const ul: any = document.getElementById("two-column-menu");
      if (!ul) return;
      const iconItems: any = ul.querySelectorAll(".nav-icon.active");
      [...iconItems].forEach((item: any) => {
        item.classList.remove("active");
        const id = item.getAttribute("sub-items");
        const getID = document.getElementById(id) as HTMLElement | null;
        getID?.classList.remove("show");
      });
    }
  }

  // twocolumn-panel: sincronizado em TwoColumnLayout por rota (folha 1 link = só ícones).
  useEffect(() => {
    if (iscurrentState !== "Dashboard") setIsDashboard(false);
    if (iscurrentState !== "Ops") setIsOps(false);
    if (iscurrentState !== "Reputation") setIsReputation(false);
    if (iscurrentState !== "Tenants") setIsTenants(false);
    if (iscurrentState !== "Plans") setIsPlans(false);
    if (iscurrentState !== "PlatformTemplates") setIsPlatformTemplates(false);
  }, [
    history,
    iscurrentState,
    isDashboard,
    isOps,
    isReputation,
    isTenants,
    isPlans,
    isPlatformTemplates,
  ]);

  // Badges ops: mount + a cada 60s (falha silenciosa — menu não deve quebrar).
  useEffect(() => {
    let cancelled = false;

    const loadBadges = () => {
      void getSaDashboardMetrics()
        .then((res) => {
          if (cancelled || !res?.success || !res.data) return;
          const m = res.data;
          const fromApi = m.menu_badges;
          setBadges(
            fromApi || {
              sends_awaiting_sa_resume: m.sends_awaiting_sa_resume ?? 0,
              sends_canary_paused: m.sends_canary_paused ?? 0,
              circuits_open: m.circuits_open_count ?? 0,
              tenants_at_risk: m.tenants_at_risk ?? 0,
              tenants_at_daily_cap: m.tenants_at_daily_cap ?? 0,
              host_stale: false,
              host_warn: false,
              dns_glue_ok: m.dns_glue?.ok !== false,
              mp_webhook_secret_alert:
                m.billing?.mp_webhook_secret_alert === true,
            }
          );
        })
        .catch(() => {
          /* ignore — badge ausente é melhor que erro no shell */
        });
    };

    loadBadges();
    const id = window.setInterval(loadBadges, 60_000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  // Prefer awaiting; se só canary, mostra canary (LAC-092).
  const sendingBadgeCount = badges
    ? badges.sends_awaiting_sa_resume > 0
      ? badges.sends_awaiting_sa_resume
      : badges.sends_canary_paused > 0
        ? badges.sends_canary_paused
        : 0
    : 0;
  const sendingBadgeColor =
    badges && badges.sends_awaiting_sa_resume > 0 ? "danger" : "warning";

  // Circuit / risco / cap diário — prioriza circuit aberto.
  const reputationBadgeCount = badges
    ? badges.circuits_open > 0
      ? badges.circuits_open
      : badges.tenants_at_risk > 0
        ? badges.tenants_at_risk
        : badges.tenants_at_daily_cap > 0
          ? badges.tenants_at_daily_cap
          : 0
    : 0;
  const reputationBadgeColor =
    badges && badges.circuits_open > 0
      ? "danger"
      : badges && badges.tenants_at_risk > 0
        ? "warning"
        : "info";

  const dashboardNeedsAttention =
    !!badges &&
    (badges.host_stale ||
      badges.mp_webhook_secret_alert ||
      badges.dns_glue_ok === false);

  const menuItems: any = [
    { label: "Menu", isHeader: true },
    {
      id: "sa-dashboard",
      label: "sa.menu.dashboard",
      icon: "ri-dashboard-2-line",
      link: "/#",
      stateVariables: isDashboard,
      click: function (e: any) {
        e.preventDefault();
        setIsDashboard(!isDashboard);
        setIscurrentState("Dashboard");
        updateIconSidebar(e);
      },
      subItems: [
        {
          id: "sa-dashboard-home",
          label: "sa.menu.dashboard",
          link: "/super-admin/dashboard",
          parentId: "sa-dashboard",
          ...(dashboardNeedsAttention
            ? {
                badgeName: "!",
                badgeColor: badges?.mp_webhook_secret_alert
                  ? "danger"
                  : "warning",
              }
            : {}),
        },
      ],
    },
    {
      id: "sa-ops",
      label: "sa.menu.ops",
      icon: "ri-pulse-line",
      link: "/#",
      stateVariables: isOps,
      click: function (e: any) {
        e.preventDefault();
        openTwoColumnSubmenu();
        setIsOps(!isOps);
        setIscurrentState("Ops");
        updateIconSidebar(e);
      },
      subItems: [
        {
          id: "sa-sending",
          label: "sa.menu.sending",
          link: "/super-admin/sending",
          parentId: "sa-ops",
          ...(sendingBadgeCount > 0
            ? {
                badgeName: String(sendingBadgeCount),
                badgeColor: sendingBadgeColor,
              }
            : {}),
        },
        {
          id: "sa-ses-endpoints",
          label: "sa.menu.sesEndpoints",
          link: "/super-admin/ses/endpoints",
          parentId: "sa-ops",
        },
        {
          id: "sa-ses-guide",
          label: "sa.menu.sesGuide",
          link: "/super-admin/ses/setup-guide",
          parentId: "sa-ops",
        },
        {
          id: "sa-ses-byo-guide",
          label: "sa.menu.sesByoGuide",
          link: "/super-admin/ses/byo-guide",
          parentId: "sa-ops",
        },
        {
          id: "sa-dns-ns-guide",
          label: "sa.menu.dnsNsGuide",
          link: "/super-admin/dns/ns-guide",
          parentId: "sa-ops",
        },
        {
          id: "sa-activity-logs",
          label: "sa.menu.activityLogs",
          link: "/super-admin/activity-logs",
          parentId: "sa-ops",
        },
        {
          id: "sa-legacy-migration",
          label: "sa.menu.legacyMigration",
          link: "/super-admin/legacy-migration",
          parentId: "sa-ops",
        },
      ],
    },
    {
      id: "sa-reputation",
      label: "sa.menu.reputation",
      icon: "ri-shield-keyhole-line",
      link: "/#",
      stateVariables: isReputation,
      ...(reputationBadgeCount > 0
        ? {
            badgeName: String(reputationBadgeCount),
            badgeColor: reputationBadgeColor,
          }
        : {}),
      click: function (e: any) {
        e.preventDefault();
        openTwoColumnSubmenu();
        setIsReputation(!isReputation);
        setIscurrentState("Reputation");
        updateIconSidebar(e);
      },
      subItems: [
        {
          id: "sa-reputation-ranking",
          label: "sa.menu.reputationRanking",
          link: "/super-admin/reputation",
          parentId: "sa-reputation",
          ...(reputationBadgeCount > 0
            ? {
                badgeName: String(reputationBadgeCount),
                badgeColor: reputationBadgeColor,
              }
            : {}),
        },
        {
          id: "sa-ses-webhooks",
          label: "sa.menu.sesWebhooks",
          link: "/super-admin/ses-webhooks",
          parentId: "sa-reputation",
        },
        {
          id: "sa-inactive-ranking",
          label: "sa.menu.inactiveRanking",
          link: "/super-admin/inactive-ranking",
          parentId: "sa-reputation",
        },
        {
          id: "sa-email-suppressions",
          label: "sa.menu.emailSuppressions",
          link: "/super-admin/email-suppressions",
          parentId: "sa-reputation",
        },
        {
          id: "sa-domain-suppressions",
          label: "sa.menu.domainSuppressions",
          link: "/super-admin/domain-suppressions",
          parentId: "sa-reputation",
        },
      ],
    },
    {
      id: "sa-tenants",
      label: "sa.menu.tenants",
      icon: "ri-building-line",
      link: "/#",
      stateVariables: isTenants,
      click: function (e: any) {
        e.preventDefault();
        setIsTenants(!isTenants);
        setIscurrentState("Tenants");
        updateIconSidebar(e);
      },
      subItems: [
        {
          id: "sa-tenants-list",
          label: "sa.menu.tenants",
          link: "/super-admin/tenants",
          parentId: "sa-tenants",
          ...(badges && badges.tenants_at_daily_cap > 0
            ? {
                badgeName: String(badges.tenants_at_daily_cap),
                badgeColor: "info",
              }
            : {}),
        },
      ],
    },
    {
      id: "sa-plans",
      label: "sa.menu.billing",
      icon: "ri-price-tag-3-line",
      link: "/#",
      stateVariables: isPlans,
      click: function (e: any) {
        e.preventDefault();
        openTwoColumnSubmenu();
        setIsPlans(!isPlans);
        setIscurrentState("Plans");
        updateIconSidebar(e);
      },
      subItems: [
        {
          id: "sa-plans-list",
          label: "sa.menu.plans",
          link: "/super-admin/plans",
          parentId: "sa-plans",
        },
        {
          id: "sa-coupons-list",
          label: "sa.menu.coupons",
          link: "/super-admin/coupons",
          parentId: "sa-plans",
        },
        {
          id: "sa-paypal-settings",
          label: "sa.menu.paypal",
          link: "/super-admin/billing/paypal",
          parentId: "sa-plans",
        },
      ],
    },
    {
      id: "sa-platform-templates",
      label: "sa.menu.platformTemplates",
      icon: "ri-layout-masonry-line",
      link: "/#",
      stateVariables: isPlatformTemplates,
      click: function (e: any) {
        e.preventDefault();
        setIsPlatformTemplates(!isPlatformTemplates);
        setIscurrentState("PlatformTemplates");
        updateIconSidebar(e);
      },
      subItems: [
        {
          id: "sa-platform-templates-list",
          label: "sa.menu.platformTemplates",
          link: "/super-admin/platform-templates",
          parentId: "sa-platform-templates",
        },
      ],
    },
  ];

  return <React.Fragment>{menuItems}</React.Fragment>;
};

export default SuperAdminNavdata;
